import React, { useState, useEffect, useCallback } from "react";
import {
  Check,
  X,
  RefreshCw,
  Loader2,
  FileText,
  MessageSquare,
  Sparkles,
  CheckCircle,
  Database,
  ClipboardList,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const TYPE_CONFIG = {
  kb_generated: {
    label: "KB Row",
    bg: "bg-violet-100",
    text: "text-violet-700",
    icon: Sparkles,
  },
  qa_pair: {
    label: "Q&A Pair",
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    icon: MessageSquare,
  },
};

const KBReviewQueue = () => {
  const { token, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [facilityId, setFacilityId] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [reviewNotes, setReviewNotes] = useState({});
  const [processing, setProcessing] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setFacilities(list);
          if (list.length > 0) setFacilityId(list[0].facility_id || "");
        }
      } catch (e) {
        console.error("Failed to load facilities:", e);
      }
    };
    load();
  }, [token]);

  const fetchItems = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/rag/documents?facility_id=${encodeURIComponent(facilityId)}&status=pending_review`,
        { headers },
      );
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load review queue:", e);
    }
    setLoading(false);
  }, [token, facilityId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleReview = async (id, action) => {
    setProcessing((prev) => new Set([...prev, id]));
    try {
      const res = await fetch(`${AUTH_URL}/rag/documents/review`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id, action, notes: reviewNotes[id] || "" }),
      });
      if (res.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
        setReviewNotes((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Review failed: ${err.error || res.status}`);
      }
    } catch (e) {
      console.error("Review failed:", e);
    }
    setProcessing((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleApproveAll = async () => {
    if (
      !window.confirm(
        `Approve all ${items.length} pending item(s)? They will be queued for embedding into Pinecone.`,
      )
    )
      return;
    setBulkProcessing(true);
    try {
      const res = await fetch(`${AUTH_URL}/rag/documents/bulk-review`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ids: items.map((i) => i.id),
          action: "approve",
        }),
      });
      if (res.ok) {
        setItems([]);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Bulk approve failed: ${err.error || res.status}`);
      }
    } catch (e) {
      console.error("Bulk approve failed:", e);
    }
    setBulkProcessing(false);
  };

  const qaCount = items.filter((i) => i.source_type === "qa_pair").length;
  const kbCount = items.filter((i) => i.source_type !== "qa_pair").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ClipboardList className="w-5 h-5 text-violet-600" />
          <div>
            <h1 className="text-lg font-semibold text-slate-800">
              KB Review Queue
            </h1>
            <p className="text-sm text-slate-500">
              Approve or deny KB rows and Q&amp;A pairs before they're embedded
              into Pinecone
            </p>
          </div>
        </div>
        <button
          onClick={fetchItems}
          disabled={loading}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center"
        >
          <RefreshCw
            className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Facility picker + stats */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select facility...</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.facility_name || f.facility_id}
            </option>
          ))}
        </select>

        {items.length > 0 && (
          <>
            <div className="flex items-center space-x-2">
              {kbCount > 0 && (
                <span className="px-3 py-1.5 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
                  {kbCount} KB row{kbCount !== 1 ? "s" : ""}
                </span>
              )}
              {qaCount > 0 && (
                <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                  {qaCount} Q&amp;A pair{qaCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            {isManager && (
              <button
                onClick={handleApproveAll}
                disabled={bulkProcessing}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
              >
                {bulkProcessing ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1" />
                )}
                Approve All
              </button>
            )}
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading review queue...
        </div>
      ) : !facilityId ? (
        <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
          Select a facility above to view pending items.
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-400" />
          <p className="font-medium text-slate-700">Queue is empty</p>
          <p className="text-sm text-slate-400 mt-1">
            No KB rows or Q&amp;A pairs are waiting for review.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {items.map((item) => {
            const typeKey =
              item.source_type === "qa_pair" ? "qa_pair" : "kb_generated";
            const tc = TYPE_CONFIG[typeKey];
            const TypeIcon = tc.icon;
            const meta = item.metadata || {};
            const isProcessing = processing.has(item.id);

            return (
              <div
                key={item.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col overflow-hidden"
              >
                {/* Card header */}
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center space-x-2 mb-1">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.text}`}
                    >
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {tc.label}
                    </span>
                    {item.chunk_total > 1 && (
                      <span className="text-xs text-slate-400">
                        Chunk {item.chunk_index + 1}/{item.chunk_total}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {item.title || item.source_filename || `Item #${item.id}`}
                  </p>
                </div>

                {/* Full content */}
                <div className="px-4 py-3 flex-1">
                  <h4 className="text-xs font-medium text-slate-500 uppercase mb-1.5">
                    Content
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 max-h-52 overflow-y-auto">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {item.content || "No content"}
                    </p>
                  </div>
                </div>

                {/* Source + metadata */}
                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-2">
                  {item.source_filename && (
                    <span className="flex items-center text-xs text-slate-500">
                      <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate max-w-[180px]">
                        {item.source_filename}
                      </span>
                    </span>
                  )}
                  {meta.category && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                      {meta.category}
                    </span>
                  )}
                  {meta.topic && (
                    <span className="px-1.5 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                      {meta.topic}
                    </span>
                  )}
                  {meta.doc_type && meta.doc_type !== "qa_pair" && (
                    <span className="px-1.5 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                      {meta.doc_type}
                    </span>
                  )}
                  {item.pinecone_namespace && (
                    <span className="flex items-center text-xs text-slate-400 ml-auto">
                      <Database className="w-3 h-3 mr-1" />
                      {item.pinecone_namespace}
                    </span>
                  )}
                </div>

                {/* Review actions */}
                {isManager && (
                  <div className="px-4 py-3 border-t border-slate-100 flex items-center space-x-2">
                    <input
                      type="text"
                      value={reviewNotes[item.id] || ""}
                      onChange={(e) =>
                        setReviewNotes((prev) => ({
                          ...prev,
                          [item.id]: e.target.value,
                        }))
                      }
                      placeholder="Optional notes..."
                      className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleReview(item.id, "approve")}
                      disabled={isProcessing}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3 mr-1" />
                      )}
                      Approve
                    </button>
                    <button
                      onClick={() => handleReview(item.id, "deny")}
                      disabled={isProcessing}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center disabled:opacity-50"
                    >
                      <X className="w-3 h-3 mr-1" /> Deny
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KBReviewQueue;
